/**
 * Suppliers Settings Tab — CR-5901
 *
 * Admin Setup → Suppliers section. Hosts the Merge Company tool that
 * consolidates two duplicate supplier records into one, plus a history
 * panel listing recent merges with their per-table impact.
 *
 * Workflow:
 *   1. Admin picks the two suppliers (Correct/retained, Incorrect/duplicate).
 *   2. "Execute" calls preview → opens a confirmation modal with impact
 *      counts. Nothing has been written yet at this point.
 *   3. "Confirm Merge" inside the modal runs the destructive action,
 *      archives the duplicate, and appends a row to the history table.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import SearchableSelect from '../../ui/SearchableSelect';
import SupplierMergePreviewDialog from './SupplierMergePreviewDialog';
import { AlertTriangle, ArrowLeft, History, Loader2, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { useFormatters } from '../../../contexts/PreferencesContext';

import {
  companyMergeAPI,
  type SelectableSupplier,
  type SupplierMergeLog,
  type SupplierMergePreview,
} from '../../../lib/api';

const NO_SELECTION = '';

export function SuppliersTab() {
  const [suppliers, setSuppliers] = useState<SelectableSupplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const { formatDateTime } = useFormatters();

  const [correctId, setCorrectId] = useState<string>(NO_SELECTION);
  const [incorrectId, setIncorrectId] = useState<string>(NO_SELECTION);

  const [preview, setPreview] = useState<SupplierMergePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [history, setHistory] = useState<SupplierMergeLog[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const r = await companyMergeAPI.selectable();
      setSuppliers(r.data.results || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load suppliers';
      toast.error(msg);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await companyMergeAPI.listHistory();
      setHistory(r.data.results || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load merge history';
      toast.error(msg);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
    loadHistory();
  }, [loadSuppliers, loadHistory]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: s.name + (s.is_active ? '' : ' (archived)'),
      })),
    [suppliers],
  );

  const correctSupplier = suppliers.find((s) => String(s.id) === correctId) || null;
  const incorrectSupplier = suppliers.find((s) => String(s.id) === incorrectId) || null;

  const canExecute =
    correctId !== NO_SELECTION &&
    incorrectId !== NO_SELECTION &&
    correctId !== incorrectId &&
    !previewing &&
    !confirming;

  const handleExecute = async () => {
    if (!canExecute) return;
    setPreviewing(true);
    try {
      const r = await companyMergeAPI.preview({
        correct_company_id: Number(correctId),
        incorrect_company_id: Number(incorrectId),
      });
      setPreview(r.data);
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Preview failed');
      toast.error(msg);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const r = await companyMergeAPI.execute({
        correct_company_id: Number(correctId),
        incorrect_company_id: Number(incorrectId),
      });
      toast.success(
        `Merged "${r.data.incorrect_company_name}" into "${r.data.correct_company_name}".`,
      );
      setPreview(null);
      setCorrectId(NO_SELECTION);
      setIncorrectId(NO_SELECTION);
      await Promise.all([loadSuppliers(), loadHistory()]);
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Merge failed');
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  const closeDialog = () => {
    if (confirming) return;
    setPreview(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Company
          </CardTitle>
          <CardDescription>
            Consolidate two duplicate supplier records into one. All
            transactional history (POs, NCRs, qualifications, etc.) is
            re-pointed at the retained supplier, customer relationships and
            their supplier codes are combined, and the duplicate is archived
            for audit purposes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSuppliers ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading suppliers…
            </div>
          ) : (
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <label className="text-sm font-medium block mb-1">
                  Correct Company <span className="text-muted-foreground">(to be kept)</span>
                </label>
                <SearchableSelect
                  options={supplierOptions}
                  value={correctId}
                  onChange={setCorrectId}
                  placeholder="Select supplier to keep"
                />
              </div>
              <div className="flex items-center pb-2">
                <ArrowLeft className="h-6 w-6 text-muted-foreground" aria-hidden />
              </div>
              <div className="flex-1 min-w-[240px]">
                <label className="text-sm font-medium block mb-1">
                  Incorrect Company <span className="text-muted-foreground">(to be merged)</span>
                </label>
                <SearchableSelect
                  options={supplierOptions}
                  value={incorrectId}
                  onChange={setIncorrectId}
                  placeholder="Select duplicate to merge"
                />
              </div>
              <Button onClick={handleExecute} disabled={!canExecute} className="min-w-[120px]">
                {previewing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Previewing…
                  </>
                ) : (
                  'Execute'
                )}
              </Button>
            </div>
          )}
          {correctId !== NO_SELECTION && correctId === incorrectId && (
            <p className="text-xs text-destructive mt-3 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              The two suppliers must be different.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Merge History
          </CardTitle>
          <CardDescription>Recent supplier merges performed on this platform.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading history…
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No supplier merges have been performed yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">By</th>
                    <th className="py-2 pr-4">Retained</th>
                    <th className="py-2 pr-4">Archived</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Moved</th>
                    <th className="py-2 pr-4">Combined</th>
                    <th className="py-2 pr-4">Already on retained</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatDateTime(row.performed_at)}
                      </td>
                      <td className="py-2 pr-4">{row.performed_by_name || '—'}</td>
                      <td className="py-2 pr-4">{row.correct_company_name}</td>
                      <td className="py-2 pr-4">{row.incorrect_company_name}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={statusVariant(row.status)}>{row.status_display}</Badge>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{row.totals?.moved ?? 0}</td>
                      <td className="py-2 pr-4 tabular-nums">{row.totals?.combined ?? 0}</td>
                      <td className="py-2 pr-4 tabular-nums">{row.totals?.already_on_kept ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierMergePreviewDialog
        preview={preview}
        correctSupplier={correctSupplier}
        incorrectSupplier={incorrectSupplier}
        confirming={confirming}
        onCancel={closeDialog}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

function statusVariant(status: SupplierMergeLog['status']): 'default' | 'secondary' | 'destructive' {
  if (status === 'success') return 'default';
  if (status === 'failed') return 'destructive';
  return 'secondary';
}

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string; detail?: string; error?: string } } }).response;
    return r?.data?.message || r?.data?.detail || r?.data?.error || fallback;
  }
  return err instanceof Error ? err.message : fallback;
}
